import { ConfirmDialog } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { ObjectiveOrDraft } from '@/types/objectives';

import { useDeleteObjective } from '@/api/objectives';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';

interface DeleteObjectiveDialogProps {
    isOpen: boolean;
    objective: ObjectiveOrDraft | null;
    onCancel: () => void;
    onSuccess: () => void;
}

/**
 * The library's confirmation dialog in its destructive form: deleting an objective cannot be
 * undone, so the confirm button takes the error colour rather than the primary one.
 */
export default function DeleteObjectiveDialog({
    isOpen,
    objective,
    onCancel,
    onSuccess,
}: DeleteObjectiveDialogProps) {
    const { t } = useTranslation();
    const { handleError } = useError();
    const deleteMutation = useDeleteObjective();
    // Deleting a shared objective takes it from every participant, which the reader must know.
    const isShared = (objective?.participantEmployeeIds.length ?? 0) > 0;

    const handleDelete = async () => {
        if (!objective) {
            return;
        }

        try {
            await deleteMutation.mutateAsync(objective.id);
            showSuccessToast({ message: t('objectives.delete.success') });
            onSuccess();
        } catch (error) {
            handleError({ error });
        }
    };

    return (
        // The dialog is mounted inside the objective tile, whose whole surface navigates to the
        // detail on click. The modal renders through a portal, but React still bubbles its clicks
        // up the component tree, so they are stopped here before they reach the tile.
        <div onClick={(event) => event.stopPropagation()}>
            <ConfirmDialog
                isOpen={isOpen}
                title={t('objectives.delete.title')}
                message={t(
                    isShared ? 'objectives.delete.sharedMessage' : 'objectives.delete.message',
                    { title: objective?.title },
                )}
                confirmLabel={t('objectives.delete.confirm')}
                cancelLabel={t('objectives.delete.cancel')}
                onConfirm={handleDelete}
                onCancel={onCancel}
                isLoading={deleteMutation.isPending}
                isDestructive
                dataTestId={'delete-objective-dialog'}
            />
        </div>
    );
}
